import { useState, useEffect, useRef } from 'react'
import { useAuth } from '../../contexts/AuthContext'
import { usePageTitle } from '../../hooks/usePageTitle'
import { supabase } from '../../lib/supabase'
import {
  VStack,
  HStack,
  Grid,
  Heading,
  Text,
  Button,
  Card,
  Input,
  Select,
  Avatar,
  Spinner,
  Animate,
  DatePicker,
  PhoneInput,
  Tabs,
  TabPanels,
  TabPanel,
  Skeleton,
  useToast
} from 'wss3-forge'
import {
  Save20Regular,
  Person20Regular,
  Camera20Regular,
  Location20Regular,
  Settings20Regular
} from '@fluentui/react-icons'

interface UserProfileData {
  id?: string
  user_id: string
  first_name?: string
  last_name?: string
  display_name?: string
  avatar_url?: string
  date_of_birth?: string
  phone?: string
  address_line1?: string
  address_line2?: string
  city?: string
  state?: string
  postal_code?: string
  country?: string
  locale?: string
  timezone?: string
}

const TIMEZONES = [
  { value: 'Europe/Paris', label: 'Europe/Paris (UTC+1)' },
  { value: 'Europe/London', label: 'Europe/London (UTC+0)' },
  { value: 'America/New_York', label: 'America/New York (UTC-5)' },
  { value: 'America/Los_Angeles', label: 'America/Los Angeles (UTC-8)' },
  { value: 'Asia/Tokyo', label: 'Asia/Tokyo (UTC+9)' },
  { value: 'Australia/Sydney', label: 'Australia/Sydney (UTC+11)' }
]

const LOCALES = [
  { value: 'en-US', label: 'English (US)' },
  { value: 'en-GB', label: 'English (UK)' },
  { value: 'fr-FR', label: 'Français (France)' },
  { value: 'de-DE', label: 'Deutsch' },
  { value: 'es-ES', label: 'Español' }
]

export function Account() {
  const { user } = useAuth()
  const toast = useToast()
  usePageTitle('My Account')
  const [profile, setProfile] = useState<UserProfileData | null>(null)
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [uploading, setUploading] = useState(false)
  const [activeTab, setActiveTab] = useState('profile')
  const initialLoadDone = useRef(false)

  useEffect(() => {
    if (user && !initialLoadDone.current) {
      loadProfile()
    }
  }, [user?.id])

  const loadProfile = async () => {
    if (!user) return

    setLoading(true)
    try {
      const { data, error } = await supabase
        .from('user_profiles')
        .select('*')
        .eq('user_id', user.id)
        .single()

      if (error && error.code !== 'PGRST116') {
        throw error
      }

      setProfile(data || {
        user_id: user.id,
        locale: 'en-US',
        timezone: 'Europe/Paris'
      })
      initialLoadDone.current = true
    } catch (err) {
      console.error('Error loading profile:', err)
      toast.error('Failed to load profile')
    } finally {
      setLoading(false)
    }
  }

  const handleSave = async () => {
    if (!user || !profile) return

    setSaving(true)

    try {
      const { error } = await supabase
        .from('user_profiles')
        .upsert({
          user_id: user.id,
          first_name: profile.first_name,
          last_name: profile.last_name,
          display_name: profile.display_name,
          avatar_url: profile.avatar_url,
          date_of_birth: profile.date_of_birth,
          phone: profile.phone,
          address_line1: profile.address_line1,
          address_line2: profile.address_line2,
          city: profile.city,
          state: profile.state,
          postal_code: profile.postal_code,
          country: profile.country,
          locale: profile.locale,
          timezone: profile.timezone
        }, {
          onConflict: 'user_id'
        })

      if (error) throw error

      toast.success('Profile saved successfully')
    } catch (err) {
      console.error('Error saving profile:', err)
      toast.error('Failed to save profile')
    } finally {
      setSaving(false)
    }
  }

  const updateField = (field: keyof UserProfileData, value: string) => {
    if (!profile) return
    setProfile({ ...profile, [field]: value })
  }

  const handleAvatarUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    if (!user || !e.target.files || e.target.files.length === 0) return

    const file = e.target.files[0]
    const fileExt = file.name.split('.').pop()
    const fileName = `${user.id}/avatar.${fileExt}`

    setUploading(true)

    try {
      const { error: uploadError } = await supabase.storage
        .from('avatars')
        .upload(fileName, file, { upsert: true })

      if (uploadError) throw uploadError

      const { data: { publicUrl } } = supabase.storage
        .from('avatars')
        .getPublicUrl(fileName)

      const avatarUrl = `${publicUrl}?t=${Date.now()}`
      setProfile(prev => prev ? { ...prev, avatar_url: avatarUrl } : null)

      await supabase
        .from('user_profiles')
        .upsert({
          user_id: user.id,
          avatar_url: avatarUrl
        }, { onConflict: 'user_id' })

      toast.success('Profile photo updated')
    } catch (err) {
      console.error('Error uploading avatar:', err)
      toast.error('Failed to upload photo')
    } finally {
      setUploading(false)
    }
  }

  const parseDateOfBirth = (): Date | null => {
    if (!profile?.date_of_birth) return null
    const date = new Date(profile.date_of_birth)
    return isNaN(date.getTime()) ? null : date
  }

  const handleDateOfBirthChange = (date: Date | null) => {
    if (!profile) return
    if (date) {
      const formatted = date.toISOString().split('T')[0]
      setProfile({ ...profile, date_of_birth: formatted })
    } else {
      setProfile({ ...profile, date_of_birth: undefined })
    }
  }

  if (loading) {
    return (
      <div className="page-content">
        <VStack gap="md">
          <VStack gap="xs">
            <Skeleton width={200} height={32} />
            <Skeleton width={280} height={20} />
          </VStack>
          <Skeleton width="100%" height={48} />
          <Card padding="md">
            <VStack gap="md">
              <Skeleton width={180} height={24} />
              <HStack gap="lg" style={{ alignItems: 'center' }}>
                <div style={{ borderRadius: '50%', overflow: 'hidden' }}><Skeleton width={64} height={64} /></div>
                <VStack gap="xs">
                  <Skeleton width={120} height={16} />
                  <Skeleton width={180} height={14} />
                </VStack>
              </HStack>
              <Grid columns={{ xs: 1, md: 2 }} gap="md">
                <Skeleton width="100%" height={56} />
                <Skeleton width="100%" height={56} />
              </Grid>
              <Grid columns={{ xs: 1, md: 2 }} gap="md">
                <Skeleton width="100%" height={56} />
                <Skeleton width="100%" height={56} />
              </Grid>
              <Skeleton width="100%" height={56} />
            </VStack>
          </Card>
        </VStack>
      </div>
    )
  }

  return (
    <div className="page-content">
      <VStack gap="md">
        <Animate type="fadeIn">
          <VStack gap="none">
            <Heading level={2}>Account Settings</Heading>
            <Text color="muted">Manage your personal information</Text>
          </VStack>
        </Animate>

        <Animate type="slideInUp" delay={50}>
          <Tabs
            tabs={[
              { id: 'profile', label: 'Profile', icon: <Person20Regular /> },
              { id: 'address', label: 'Address', icon: <Location20Regular /> },
              { id: 'preferences', label: 'Preferences', icon: <Settings20Regular /> }
            ]}
            active={activeTab}
            onChange={setActiveTab}
            stretchLine
          />
        </Animate>

        <TabPanels active={activeTab}>
          {/* Profile Tab */}
          <TabPanel id="profile" active={activeTab}>
            <Card padding="md">
              <VStack gap="md">
                <HStack gap="sm" style={{ alignItems: 'center', marginBottom: '0.5rem' }}>
                  <Person20Regular style={{ color: 'var(--brand-primary)' }} />
                  <Heading level={5}>Personal Information</Heading>
                </HStack>

                <HStack gap="lg" style={{ alignItems: 'center', flexWrap: 'wrap' }}>
                  <div style={{ position: 'relative' }}>
                    <Avatar
                      name={profile?.display_name || profile?.first_name || user?.email || 'User'}
                      src={profile?.avatar_url}
                      size="xl"
                    />
                    <label
                      htmlFor="avatar-upload"
                      style={{
                        position: 'absolute',
                        bottom: 0,
                        right: 0,
                        width: '28px',
                        height: '28px',
                        borderRadius: '50%',
                        backgroundColor: 'var(--brand-primary)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        cursor: uploading ? 'wait' : 'pointer',
                        border: '2px solid var(--bg-primary)'
                      }}
                    >
                      {uploading ? (
                        <Spinner size="sm" />
                      ) : (
                        <Camera20Regular style={{ color: 'white', fontSize: '14px' }} />
                      )}
                    </label>
                    <input
                      id="avatar-upload"
                      type="file"
                      accept="image/*"
                      onChange={handleAvatarUpload}
                      disabled={uploading}
                      style={{ display: 'none' }}
                    />
                  </div>
                  <VStack gap="xs">
                    <Text weight="medium">{profile?.display_name || 'Not set'}</Text>
                    <Text size="sm" color="muted">{user?.email}</Text>
                    <Text size="xs" color="muted">Click the icon to change photo</Text>
                  </VStack>
                </HStack>

                <Grid columns={{ xs: 1, md: 2 }} gap="md">
                  <Input
                    label="First name"
                    value={profile?.first_name || ''}
                    onChange={(value) => updateField('first_name', value as string)}
                    placeholder="First name"
                  />
                  <Input
                    label="Last name"
                    value={profile?.last_name || ''}
                    onChange={(value) => updateField('last_name', value as string)}
                    placeholder="Last name"
                  />
                </Grid>

                <Grid columns={{ xs: 1, md: 2 }} gap="md">
                  <Input
                    label="Display name"
                    value={profile?.display_name || ''}
                    onChange={(value) => updateField('display_name', value as string)}
                    placeholder="How would you like to be called?"
                  />
                  <DatePicker
                    label="Date of birth"
                    value={parseDateOfBirth()}
                    onChange={handleDateOfBirthChange}
                    placeholder="Select date"
                    maxDate={new Date()}
                  />
                </Grid>

                <PhoneInput
                  label="Phone number"
                  value={profile?.phone || ''}
                  onChange={(value) => updateField('phone', value)}
                  defaultCountry="FR"
                />

                <HStack gap="sm" style={{ justifyContent: 'flex-end', marginTop: '0.5rem' }}>
                  <Button
                    icon={<Save20Regular />}
                    onClick={handleSave}
                    loading={saving}
                    disabled={saving}
                  >
                    Save changes
                  </Button>
                </HStack>
              </VStack>
            </Card>
          </TabPanel>

          {/* Address Tab */}
          <TabPanel id="address" active={activeTab}>
            <Card padding="md">
              <VStack gap="md">
                <HStack gap="sm" style={{ alignItems: 'center', marginBottom: '0.5rem' }}>
                  <Location20Regular style={{ color: 'var(--brand-primary)' }} />
                  <Heading level={5}>Address</Heading>
                </HStack>

                <Input
                  label="Address line 1"
                  value={profile?.address_line1 || ''}
                  onChange={(value) => updateField('address_line1', value as string)}
                  placeholder="123 Main Street"
                />

                <Input
                  label="Address line 2"
                  value={profile?.address_line2 || ''}
                  onChange={(value) => updateField('address_line2', value as string)}
                  placeholder="Apartment, suite, etc."
                />

                <Grid columns={{ xs: 1, md: 3 }} gap="md">
                  <Input
                    label="City"
                    value={profile?.city || ''}
                    onChange={(value) => updateField('city', value as string)}
                    placeholder="New York"
                  />
                  <Input
                    label="Postal code"
                    value={profile?.postal_code || ''}
                    onChange={(value) => updateField('postal_code', value as string)}
                    placeholder="10001"
                  />
                  <Input
                    label="Country (ISO)"
                    value={profile?.country || ''}
                    onChange={(value) => updateField('country', value as string)}
                    placeholder="US"
                    maxLength={2}
                  />
                </Grid>

                <HStack gap="sm" style={{ justifyContent: 'flex-end', marginTop: '0.5rem' }}>
                  <Button
                    icon={<Save20Regular />}
                    onClick={handleSave}
                    loading={saving}
                    disabled={saving}
                  >
                    Save changes
                  </Button>
                </HStack>
              </VStack>
            </Card>
          </TabPanel>

          {/* Preferences Tab */}
          <TabPanel id="preferences" active={activeTab}>
            <Card padding="md">
              <VStack gap="md">
                <HStack gap="sm" style={{ alignItems: 'center', marginBottom: '0.5rem' }}>
                  <Settings20Regular style={{ color: 'var(--brand-primary)' }} />
                  <Heading level={5}>Preferences</Heading>
                </HStack>

                <Grid columns={{ xs: 1, md: 2 }} gap="md">
                  <Select
                    label="Language"
                    value={profile?.locale || 'en-US'}
                    onChange={(value) => updateField('locale', value)}
                    options={LOCALES}
                  />
                  <Select
                    label="Timezone"
                    value={profile?.timezone || 'Europe/Paris'}
                    onChange={(value) => updateField('timezone', value)}
                    options={TIMEZONES}
                  />
                </Grid>

                <HStack gap="sm" style={{ justifyContent: 'flex-end', marginTop: '0.5rem' }}>
                  <Button
                    icon={<Save20Regular />}
                    onClick={handleSave}
                    loading={saving}
                    disabled={saving}
                  >
                    Save changes
                  </Button>
                </HStack>
              </VStack>
            </Card>
          </TabPanel>
        </TabPanels>
      </VStack>
    </div>
  )
}
